import { useCallback, useState } from 'react';
import { DocumentInfo } from './DocumentItem';
import { useTranslation } from 'react-i18next';
import { type Selector, useFootnotes } from '@n2-common/lib/useFootnotes';
import { Button, DismissableErrorMessage, Headline, LightBox, Paragraph } from '@n2-common/ui';
import style from './style.module.scss';
import FileUploadButton from '../FileUploadButton';
import { Uploads } from './Uploads';

import { useCmsNotification } from '../CmsNotification';
import { QuestionIcon } from '@n2-common/myPagesUi/components/icons';

export function SingleUploadBox({ requirement, uploadDocument, deleteDocument, trustedUpload, footnoteSelector }: SingleUploadBoxProps): JSX.Element {
    const [working, setWorking] = useState(false);
    const { t } = useTranslation('common');
    const notifications = useCmsNotification(`applicationforms_document_requirement_${requirement.documentType}`);

    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const uploadDocumentHandler = useCallback(
        async (form: FormData) => {
            setWorking(true);
            setErrorMsg(null);
            try {
                await uploadDocument(form);
                setWorking(false);
            } catch (err) {
                setErrorMsg(t<string>('Sign.Documents.UploadError'));
                setWorking(false);
            }
        },
        [t, uploadDocument]
    );

    const deleteDocumentHandler = useCallback(
        async (documentId: string) => {
            setWorking(true);
            setErrorMsg(null);
            try {
                await deleteDocument(documentId);
                setWorking(false);
            } catch (err) {
                setErrorMsg(t<string>('Sign.Documents.DeleteError'));
                setWorking(false);
            }
        },
        [deleteDocument, t]
    );

    const [footnotes] = useFootnotes(requirement.uploadedDocuments, footnoteSelector);

    return (
        <div className={style.singleUploadBox}>
            <div className={style.buttons}>
                {requirement.canUseTrusted && (
                    <Button
                        uiType="secondary"
                        disabled={requirement.uploadedDocuments.some((d) => d.trusted)}
                        className={style.sblButton}
                        onClick={trustedUpload}
                    >
                        {t<string>('Sign.Documents.Trusted.ButtonText')}
                    </Button>
                )}
                {requirement.canUpload && (
                    <FileUploadButton
                        uploadDocument={uploadDocumentHandler}
                        title={`${t<string>('Sign.Documents.UploadTitle')} ${requirement.help.heading}`}
                        buttonLabel={t<string>('Sign.Documents.UploadButtonTitle')}
                        uploadingLabel={t<string>('Sign.Documents.UploadInProgress')}
                    />
                )}
            </div>
            <DismissableErrorMessage error={errorMsg} onDismiss={() => setErrorMsg(null)} />
            <div className={style.heading}>
                {requirement.help.heading}
                {requirement.help.description && <LightBox
                    url={requirement.help.imageUrl}
                    icon={<QuestionIcon className={style.questionMark} />}
                    nameforuitesting={requirement.documentType}
                    description={
                        requirement.help.description ? (
                            <div dangerouslySetInnerHTML={{ __html: requirement.help.description }} className={style.description} />
                        ) : undefined
                    }
                />}
            </div>
            <Uploads
                documents={requirement.uploadedDocuments}
                refreshing={working}
                deleteDocumentHandler={deleteDocumentHandler}
                footnoteSelector={footnoteSelector}
            />
            <div className={style.footnotes}>
                {requirement.completed ? <Paragraph>{t<string>('Sign.Documents.FullyDocumentedDescription')}</Paragraph> : null}
                {footnotes}
                {notifications.map((note, i) => (
                    <div key={i}>
                        <Headline level={3}>{note.title}</Headline>
                        <Paragraph>{note.description}</Paragraph>
                    </div>
                ))}
            </div>
        </div>
    );
}

export type DocumentRequirement = {
    uploadedDocuments: DocumentInfo[];
    help: HelpInfo;
    documentType: string;
    canUseTrusted: boolean;
    canUpload: boolean;
    completed: boolean;
};

type HelpInfo = {
    heading: string;
    description: string;
    imageUrl?: string;
};

type SingleUploadBoxProps = {
    requirement: DocumentRequirement;
    trustedUpload: () => Promise<void>;
    uploadDocument: (data: FormData) => Promise<void>;
    deleteDocument: (documentId: string) => Promise<void>;
    footnoteSelector: Selector<DocumentInfo>;
};
